import { NotificationDeliveryStatus } from "@/api-schema-enums";
import { useNotificationTestSendMutation } from "@/common/api/notification/use-notification-test-send.mutation";
import { handleFormSubmission } from "@/common/helpers/form-submission";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { Badge, BadgeColorVariant, BadgeVariant } from "@/components/ui/badge";
import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { zodResolver } from "@hookform/resolvers/zod";
import type { TFunction } from "i18next";
import { type ReactElement } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

function createSchema(t: TFunction<"notification">) {
  return z.object({
    recipientEmail: z.email(t("EmailRequired")),
    title: z.string().min(1, t("TitleRequired")),
    message: z.string().min(1, t("MessageRequired")),
  });
}

type NotificationTestFormValues = z.infer<ReturnType<typeof createSchema>>;

export function NotificationView(): ReactElement {
  const { t } = useTranslation("notification");
  const mutation = useNotificationTestSendMutation();

  const form = useForm<NotificationTestFormValues>({
    resolver: zodResolver(createSchema(t)),
    mode: "onBlur",
    defaultValues: { recipientEmail: "", title: "", message: "" },
  });

  const onSubmit = async (values: NotificationTestFormValues): Promise<void> => {
    await handleFormSubmission(() => mutation.mutateAsync(values), {
      success: t("SendSuccess"),
      error: t("SendError"),
    });
  };

  return (
    <CenteredLayout
      variant={CenteredLayoutVariant.Form}
      title={t("TestNotifications")}
      subTitle={t("TestNotificationsDescription")}
    >
      <Card>
        <CardContent>
          <Form {...form}>
            <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
              <FormField
                control={form.control}
                name="recipientEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("RecipientEmail")}</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder={t("RecipientEmailPlaceholder")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("Title")}</FormLabel>
                    <FormControl>
                      <Input placeholder={t("TitlePlaceholder")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("Message")}</FormLabel>
                    <FormControl>
                      <Textarea placeholder={t("MessagePlaceholder")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end">
                <Button
                  type="submit"
                  variant={ButtonVariant.Filled}
                  colorVariant={ButtonColorVariant.Primary}
                  disabled={mutation.isPending || !form.formState.isValid}
                >
                  {t("Send")}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      {mutation.data && (
        <Card>
          <CardContent className="space-y-3">
            <h2 className="text-sm font-medium">{t("Results")}</h2>
            <p className="text-muted-foreground text-sm">{t("ResultsDescription")}</p>
            <ul className="space-y-2">
              {mutation.data.results.map((result) => (
                <li key={result.channel} className="flex items-center justify-between gap-4 text-sm">
                  <span className="font-mono">{result.channel}</span>
                  <Badge
                    variant={BadgeVariant.Filled}
                    colorVariant={
                      result.status === NotificationDeliveryStatus.Sent
                        ? BadgeColorVariant.Success
                        : BadgeColorVariant.Error
                    }
                  >
                    {result.status === NotificationDeliveryStatus.Sent
                      ? t("DeliveryStatus.Processed")
                      : `${t("DeliveryStatus.Failed")}${result.error ? ` — ${result.error}` : ""}`}
                  </Badge>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </CenteredLayout>
  );
}
